<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useLabelVisibility } from 'dashboard/composables/useLabelVisibility';
import {
  useSidebarTopicDialog,
  validTopicForm,
} from 'dashboard/composables/useSidebarTopics';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const { t } = useI18n();
const labelUIFlags = useMapGetter('labels/getUIFlags');
const getTopicById = useMapGetter('mhTopics/getTopicById');
const {
  labels,
  visibleTeams,
  teamLabelGroups,
  teamLabels,
  sharedLabels,
  visibleLabels,
} = useLabelVisibility();
const { topicDialogState, closeTopicDialog } = useSidebarTopicDialog();

const dialogRef = ref(null);
const name = ref('');
const selectedLabelIds = ref([]);
const isSaving = ref(false);

const isEditing = computed(() => topicDialogState.value?.mode === 'edit');
const activeTopic = computed(() =>
  getTopicById.value(topicDialogState.value?.topicId)
);
const visibleLabelIds = computed(
  () => new Set(visibleLabels.value.map(label => label.id))
);
const liveLabelIds = computed(
  () => new Set(labels.value.map(label => label.id))
);
const liveSelectedLabelIds = computed(() =>
  selectedLabelIds.value.filter(id => liveLabelIds.value.has(id))
);
const labelsReady = computed(() => !labelUIFlags.value.isFetching);
const showTeamGroups = computed(() => visibleTeams.value.length > 1);
const allSelectedLabelsHidden = computed(
  () =>
    isEditing.value &&
    liveSelectedLabelIds.value.length > 0 &&
    liveSelectedLabelIds.value.every(id => !visibleLabelIds.value.has(id))
);
const dialogTitle = computed(() => {
  if (isEditing.value) return t('MH_TOPICS.DIALOG.EDIT_TITLE');
  return t('MH_TOPICS.DIALOG.CREATE_TITLE');
});
const canSave = computed(() =>
  labelsReady.value
    ? validTopicForm(name.value, liveSelectedLabelIds.value)
    : false
);

const toggleLabel = labelId => {
  selectedLabelIds.value = selectedLabelIds.value.includes(labelId)
    ? selectedLabelIds.value.filter(id => id !== labelId)
    : [...selectedLabelIds.value, labelId];
};

const populateForm = () => {
  if (!isEditing.value) {
    name.value = '';
    selectedLabelIds.value = [];
    return;
  }

  name.value = activeTopic.value?.name || '';
  selectedLabelIds.value = [...(activeTopic.value?.label_ids || [])];
};

watch(topicDialogState, async state => {
  if (!state) return;
  populateForm();
  await nextTick();
  dialogRef.value?.open();
});

const handleClose = () => closeTopicDialog();

const handleConfirm = async () => {
  if (!canSave.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const attributes = {
      name: name.value.trim(),
      label_ids: liveSelectedLabelIds.value,
    };
    if (isEditing.value) {
      await store.dispatch('mhTopics/update', {
        id: activeTopic.value.id,
        lock_version: activeTopic.value.lock_version,
        ...attributes,
      });
    } else {
      await store.dispatch('mhTopics/create', attributes);
    }
    dialogRef.value?.close();
  } catch (error) {
    if (error?.response?.status === 409) {
      await store.dispatch('mhTopics/get');
      useAlert(t('MH_TOPICS.ERRORS.STALE_OBJECT'));
    } else {
      useAlert(t('MH_TOPICS.ERRORS.SAVE'));
    }
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :confirm-button-label="t('MH_TOPICS.DIALOG.SAVE')"
    :cancel-button-label="t('MH_TOPICS.DIALOG.CANCEL')"
    :disable-confirm-button="!canSave"
    :is-loading="isSaving"
    overflow-y-auto
    width="md"
    @confirm="handleConfirm"
    @close="handleClose"
  >
    <div class="grid gap-4">
      <Input
        v-model="name"
        autofocus
        maxlength="60"
        :label="t('MH_TOPICS.DIALOG.NAME')"
        :placeholder="t('MH_TOPICS.DIALOG.NAME_PLACEHOLDER')"
        @enter="handleConfirm"
      />
      <div class="grid gap-2">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('MH_TOPICS.DIALOG.LABELS') }}
        </p>
        <p
          v-if="allSelectedLabelsHidden"
          class="mb-0 rounded-md bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11"
        >
          {{ t('MH_TOPICS.DIALOG.ALL_LABELS_HIDDEN') }}
        </p>
        <div v-if="visibleLabels.length" class="grid gap-3">
          <template v-if="showTeamGroups">
            <section
              v-for="(group, index) in teamLabelGroups"
              :key="group.id"
              class="grid gap-2"
              :class="{ 'border-t border-n-weak pt-3': index > 0 }"
            >
              <p class="mb-0 text-xs font-medium text-n-slate-11">
                {{ group.name }}
              </p>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="label in group.labels"
                  :key="label.id"
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                  :class="
                    selectedLabelIds.includes(label.id)
                      ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  @click="toggleLabel(label.id)"
                >
                  <span
                    class="size-2 rounded-full"
                    :style="{ backgroundColor: label.color }"
                  />
                  {{ label.title }}
                </button>
              </div>
            </section>
          </template>
          <div v-else-if="teamLabels.length" class="flex flex-wrap gap-1.5">
            <button
              v-for="label in teamLabels"
              :key="label.id"
              type="button"
              class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors"
              :class="
                selectedLabelIds.includes(label.id)
                  ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="toggleLabel(label.id)"
            >
              <span
                class="size-2 rounded-full"
                :style="{ backgroundColor: label.color }"
              />
              {{ label.title }}
            </button>
          </div>
          <section
            v-if="sharedLabels.length"
            class="grid gap-2"
            :class="{ 'border-t border-n-weak pt-3': teamLabels.length }"
          >
            <p class="mb-0 text-xs font-medium text-n-slate-11">
              {{ t('MH_TOPICS.DIALOG.SHARED_LABELS') }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="label in sharedLabels"
                :key="label.id"
                type="button"
                class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                :class="
                  selectedLabelIds.includes(label.id)
                    ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="toggleLabel(label.id)"
              >
                <span
                  class="size-2 rounded-full"
                  :style="{ backgroundColor: label.color }"
                />
                {{ label.title }}
              </button>
            </div>
          </section>
        </div>
        <p
          v-else-if="!allSelectedLabelsHidden"
          class="mb-0 text-xs text-n-slate-10"
        >
          {{ t('MH_TOPICS.DIALOG.NO_LABELS') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
